import React from 'react';
import { Order } from '@/types';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  TruckIcon, 
  XCircleIcon,
  CreditCardIcon,
  UserIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

interface OrderTrackingTimelineProps {
  order: Order;
}

interface TimelineStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  status: 'completed' | 'current' | 'pending';
  date?: string;
}

const OrderTrackingTimeline: React.FC<OrderTrackingTimelineProps> = ({ order }) => {
  const getTimelineSteps = (): TimelineStep[] => {
    const steps: TimelineStep[] = [
      {
        id: 'order_placed',
        title: 'Order Placed',
        description: 'Your order has been placed successfully',
        icon: CreditCardIcon,
        status: 'completed',
        date: order.created_at
      },
      {
        id: 'order_confirmed',
        title: 'Order Confirmed',
        description: 'Your order has been confirmed by admin',
        icon: CheckCircleIcon,
        status: order.order_status === 'pending' ? 'pending' : 'completed',
        date: order.confirmed_at
      },
      {
        id: 'processing',
        title: 'Processing',
        description: 'Your order is being prepared for shipment',
        icon: UserIcon,
        status: ['confirmed', 'shipped', 'delivered'].includes(order.order_status) ? 'completed' : 
                order.order_status === 'processing' ? 'current' : 'pending'
      },
      {
        id: 'shipped',
        title: 'Shipped',
        description: 'Your order has been shipped and is on its way',
        icon: TruckIcon,
        status: order.order_status === 'shipped' ? 'current' : 
                order.order_status === 'delivered' ? 'completed' : 'pending',
        date: order.shipped_at
      },
      {
        id: 'delivered',
        title: 'Delivered',
        description: 'Your order has been delivered successfully',
        icon: MapPinIcon,
        status: order.order_status === 'delivered' ? 'completed' : 'pending',
        date: order.delivered_at
      }
    ];

    // Add cancelled step if order is cancelled
    if (order.order_status === 'cancelled') {
      steps.push({
        id: 'cancelled',
        title: 'Order Cancelled',
        description: 'Your order has been cancelled',
        icon: XCircleIcon,
        status: 'completed',
        date: order.cancelled_at
      });
    }

    return steps;
  };

  const steps = getTimelineSteps();

  const getStepIcon = (step: TimelineStep) => {
    const IconComponent = step.icon;
    
    if (step.status === 'completed') {
      return <CheckCircleSolidIcon className="h-6 w-6 text-green-500" />;
    } else if (step.status === 'current') {
      return <IconComponent className="h-6 w-6 text-primary-600" />;
    } else {
      return <IconComponent className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStepColor = (step: TimelineStep) => {
    if (step.status === 'completed') {
      return 'border-green-500 bg-green-50';
    } else if (step.status === 'current') {
      return 'border-primary-500 bg-primary-50';
    } else {
      return 'border-gray-300 bg-gray-50';
    }
  };

  const getTextColor = (step: TimelineStep) => {
    if (step.status === 'completed') {
      return 'text-green-700';
    } else if (step.status === 'current') {
      return 'text-primary-700';
    } else {
      return 'text-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Order Tracking</h3>
      
      <div className="space-y-6">
        {steps.map((step, index) => (
          <div key={step.id} className="relative">
            {/* Timeline line */}
            {index < steps.length - 1 && (
              <div className={`absolute left-6 top-12 w-0.5 h-16 ${
                step.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
              }`} />
            )}
            
            <div className={`flex items-start space-x-4 p-4 rounded-lg border-2 ${getStepColor(step)}`}>
              <div className="flex-shrink-0">
                {getStepIcon(step)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className={`font-medium ${getTextColor(step)}`}>
                    {step.title}
                  </h4>
                  {step.date && (
                    <span className="text-sm text-gray-500">
                      {new Date(step.date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-1 ${getTextColor(step)}`}>
                  {step.description}
                </p>
                
                {/* Additional info for specific steps */}
                {step.id === 'shipped' && order.tracking_number && (
                  <div className="mt-2 p-2 bg-white rounded border">
                    <p className="text-sm font-medium text-gray-700">Tracking Number:</p>
                    <p className="text-sm text-gray-600 font-mono">{order.tracking_number}</p>
                  </div>
                )}
                
                {step.id === 'delivered' && order.delivery_notes && (
                  <div className="mt-2 p-2 bg-white rounded border">
                    <p className="text-sm font-medium text-gray-700">Delivery Notes:</p>
                    <p className="text-sm text-gray-600">{order.delivery_notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Estimated delivery */}
      {order.order_status !== 'delivered' && order.order_status !== 'cancelled' && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-blue-800">Estimated Delivery</p>
              <p className="text-sm text-blue-600">
                {order.estimated_delivery_date 
                  ? new Date(order.estimated_delivery_date).toLocaleDateString()
                  : '3-5 business days from confirmation'
                }
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingTimeline;
